/**
 * RefId Selector - TICKET JXWPR9
 * 
 * Selector de agentes VRT o labs EPL con búsqueda.
 * Carga catálogos desde API admin.
 */

import React, { useState, useEffect } from 'react';
import { Search, X, Loader, AlertCircle, Check } from 'lucide-react';
import apiClient from '../../services/apiClient';

const RefIdSelector = ({ type, currentRefId, onSelect, onClose }) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Determinar endpoint según tipo (apiClient ya tiene /api en baseURL)
  const endpoint = type === 'vrt_simulation' 
    ? '/admin/courses/vrt/agents?status=published'
    : '/admin/courses/epl/labs?status=active';

  const title = type === 'vrt_simulation' 
    ? 'Seleccionar agente VRT'
    : 'Seleccionar lab EPL';

  const emptyMessage = type === 'vrt_simulation'
    ? 'No hay agentes VRT publicados disponibles'
    : 'No hay labs EPL activos disponibles';

  // Cargar catálogo
  useEffect(() => {
    const loadCatalog = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(endpoint);
        console.log(`📦 Catálogo ${type}:`, response.data);
        console.log(`📦 Ejemplo primer item:`, response.data[0]);
        setItems(response.data);
        setFilteredItems(response.data);
      } catch (err) {
        console.error(`Error loading ${type} catalog:`, err);
        setError(err.message || 'Error al cargar catálogo');
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, [endpoint, type]);

  // Filtrar por búsqueda
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = items.filter(item => {
      const searchText = type === 'vrt_simulation'
        ? `${item.visibleName} ${item.agentId}`.toLowerCase()
        : `${item.title} ${item.labId}`.toLowerCase();
      
      return searchText.includes(query);
    });

    setFilteredItems(filtered);
  }, [searchQuery, items, type]);

  // Obtener ID del item
  const getItemId = (item) => {
    return type === 'vrt_simulation' ? item.agentId : item.labId;
  };

  // Obtener nombre del item
  const getItemName = (item) => {
    return type === 'vrt_simulation' ? item.visibleName : item.title;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={type === 'vrt_simulation' ? 'Buscar por nombre o agentId...' : 'Buscar por título o labId...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-8 h-8 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Cargando catálogo...</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Error al cargar catálogo</p>
                <p className="text-xs mt-1">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600">
                {searchQuery ? 'No se encontraron resultados' : emptyMessage}
              </p>
            </div>
          )}

          {!loading && !error && filteredItems.length > 0 && (
            <div className="space-y-2">
              {filteredItems.map((item) => {
                const itemId = getItemId(item);
                const isSelected = currentRefId === itemId;

                return (
                  <button
                    key={itemId}
                    onClick={() => onSelect(itemId)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900 truncate">
                            {getItemName(item)}
                          </h4>
                          {isSelected && (
                            <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 font-mono">
                          ID: {itemId}
                        </p>
                        {item.systemCard && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs font-medium">
                            {item.systemCard}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {filteredItems.length} {type === 'vrt_simulation' ? 'agentes' : 'labs'} disponibles
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefIdSelector;
